import { useCallback, useState } from 'react';
import { messageFrom } from '@bp-files/client';
import {
  getNotifications,
  subscribeToFolder,
  unsubscribeFromFolder,
  type NotificationsBody,
} from '@/lib/api';

/**
 * The notifications switch on a folder (NTF-1, NTF-4).
 *
 * **Two facts, and they are deliberately not the same question.** Whether *this
 * folder* is subscribed rides on the folder listing, so it arrives with the
 * grid and is passed in here rather than fetched — the switch renders on first
 * paint with no request of its own. Whether *this person* has accepted the
 * statement is per-account, is fetched once, and is what decides whether
 * pressing the switch needs a dialog at all.
 *
 * Conflating them would put a consent dialog in front of somebody who already
 * agreed on a different folder.
 *
 * **The consent read is lazy.** It happens when somebody first reaches for the
 * switch, not on every folder view: the overwhelming majority of visits never
 * touch it, and a `GET /me/notifications` per folder open would be a request
 * per page load to answer a question nobody asked.
 *
 * **This is not authorization.** NTF-6 decides delivery from current state at
 * send time; a subscription row grants nothing, and neither does this hook.
 */

export type SubscriptionPhase =
  | { kind: 'idle' }
  /** Fetching the statement, because somebody just reached for the switch. */
  | { kind: 'checking' }
  /** The dialog is up. `clickwrap` is what it must display, verbatim. */
  | { kind: 'consenting'; clickwrap: NotificationsBody['clickwrap']; error?: string }
  /** A subscribe or unsubscribe is in flight. */
  | { kind: 'saving' }
  | { kind: 'error'; message: string };

export interface UseFolderSubscriptionResult {
  subscribed: boolean;
  phase: SubscriptionPhase;
  /** The switch was pressed. Decides for itself whether a dialog is needed. */
  toggle: () => void;
  /** The dialog's confirm. */
  accept: () => void;
  /** The dialog's cancel, Escape, or overlay. Never an acceptance. */
  dismiss: () => void;
}

export function useFolderSubscription(
  spaceId: string | undefined,
  folderId: string | undefined,
  /** From the folder listing — the server's answer, not a guess. */
  initiallySubscribed: boolean,
  /**
   * What to say when the API gave us nothing to say. Passed in from the
   * caller's `*.copy.ts` rather than defined here — every user-facing string
   * lives in a copy file, and `messageFrom` prefers the server's own line
   * anyway, which for a stale clickwrap names the actual remedy.
   */
  failureMessage: string,
  /** Refresh the grid, so the flag this hook was seeded from catches up. */
  onChanged?: () => void,
): UseFolderSubscriptionResult {
  const [subscribed, setSubscribed] = useState(initiallySubscribed);
  const [phase, setPhase] = useState<SubscriptionPhase>({ kind: 'idle' });
  const [seeded, setSeeded] = useState(initiallySubscribed);

  /*
    The grid is the source of truth and it reloads on its own schedule — after
    an upload, after a delete, after a navigation. Following it means the switch
    corrects itself rather than drifting from what the server last said.

    **Adjusted during render rather than in an effect**, which is React's own
    documented shape for "reset state when a prop changes" and what the lint
    rule about synchronous `setState` in an effect points at. An effect would
    paint the stale value first and then correct it — a switch that visibly
    flips on its own after the grid reloads.

    Local state at all, rather than reading the prop directly, because the
    refresh is asynchronous: between the server confirming and the grid coming
    back, the prop still says what it said before, and a switch that snapped
    back for that window would read as a failure.
  */
  if (initiallySubscribed !== seeded) {
    setSeeded(initiallySubscribed);
    setSubscribed(initiallySubscribed);
  }

  const save = useCallback(
    async (next: boolean, acceptedVersion?: number) => {
      if (spaceId === undefined || folderId === undefined) return;

      setPhase({ kind: 'saving' });
      try {
        if (next) {
          if (acceptedVersion === undefined) return;
          await subscribeToFolder(spaceId, folderId, acceptedVersion);
        } else {
          await unsubscribeFromFolder(spaceId, folderId);
        }
        setSubscribed(next);
        setPhase({ kind: 'idle' });
        onChanged?.();
      } catch (error) {
        setPhase({ kind: 'error', message: messageFrom(error, failureMessage) });
      }
    },
    [spaceId, folderId, onChanged, failureMessage],
  );

  const toggle = useCallback(() => {
    if (spaceId === undefined || folderId === undefined) return;
    if (phase.kind === 'saving' || phase.kind === 'checking') return;

    // Turning it off needs no consent, and must not ask for one: somebody who
    // disagrees with new wording is exactly who is reaching for this.
    if (subscribed) {
      void save(false);
      return;
    }

    setPhase({ kind: 'checking' });
    void (async () => {
      try {
        const { clickwrap, accepted } = await getNotifications();

        /*
          Already accepted *this* version — subscribe without a dialog. The
          comparison is against the version the server just handed back rather
          than a constant, which is what makes a bumped statement re-ask
          everybody exactly once.
        */
        if (accepted !== null && accepted.version === clickwrap.version) {
          await save(true, clickwrap.version);
          return;
        }

        setPhase({ kind: 'consenting', clickwrap });
      } catch (error) {
        setPhase({ kind: 'error', message: messageFrom(error, failureMessage) });
      }
    })();
  }, [spaceId, folderId, phase.kind, subscribed, save, failureMessage]);

  const accept = useCallback(() => {
    if (phase.kind !== 'consenting') return;
    void save(true, phase.clickwrap.version);
  }, [phase, save]);

  const dismiss = useCallback(() => {
    setPhase({ kind: 'idle' });
  }, []);

  return { subscribed, phase, toggle, accept, dismiss };
}
